import { Fragment } from 'react';
import { FieldRenderer } from './FieldRenderer';
import { sectionVisibleFields, type SectionSpec } from './sections';
import type { ConnectorFieldSchema } from '@/types/connector-schema';
import type { Advertiser } from '@/components/connectors/form/LoadAdvertisersButton';
import { getFieldEnhancement } from '@/components/connectors/wizard/dynamic/enhancements';

const FULL_WIDTH_TYPES = new Set([
  'multi_select_checkbox', 'multi_select_checkbox_dynamic',
  'oauth_button', 'oauth_populated_dropdown', 'load_advertisers_button',
  'textarea', 'boolean',
]);

const STORED_FLAG_BY_FIELD: Record<string, string> = {
  password: 'has_password',
  client_secret: 'has_client_secret',
  client_id: 'has_client_id',
  api_key: 'has_api_key',
  api_token: 'has_api_token',
  refresh_token: 'has_refresh_token',
  access_token: 'has_access_token',
  portal_id: 'has_portal_id',
};

interface Props {
  section: SectionSpec;
  schemaFields: ConnectorFieldSchema[];
  values: Record<string, unknown>;
  dirty: Record<string, boolean>;
  /** Per-field validation messages, keyed by field name (#1163). Only fields the
   *  save actually sends (dirty ∩ visible) ever appear here. */
  errors?: Record<string, string>;
  credentialFlags: Record<string, boolean>;
  connectorType: string;
  /** Connector instance name (e.g. "My Google Ads"). Forwarded to FieldRenderer
   *  so stored-creds OAuth dropdowns can fetch options by connector name (#143). */
  connectorName: string;
  disabled?: boolean;
  onChange: (name: string, value: unknown) => void;
  onBatchFieldChange?: (updates: Record<string, unknown>) => void;
  /** Connected-identity per OAuth field (#140). null until backend lands. */
  oauthIdentity?: string | null;
  /** Called by OAuthRow after Reconnect. */
  onReconnected?: () => void;
  /** Runtime cache of advertiser lists returned by LOAD_ADVERTISERS_BUTTON fields. */
  loaderResults?: Record<string, Advertiser[]>;
  /** Callback fired when a LOAD_ADVERTISERS_BUTTON completes a fetch. */
  onLoaderResult?: (fieldName: string, advertisers: Advertiser[]) => void;
}

export function SectionBody({
  section, schemaFields, values, dirty, errors, credentialFlags, connectorType, connectorName,
  disabled, onChange, onBatchFieldChange, oauthIdentity, onReconnected,
  loaderResults, onLoaderResult,
}: Props) {
  const cols = section.cols ?? 1;
  const byName = new Map(schemaFields.map((f) => [f.name, f]));

  // Honour the backend schema's visible_if rules (e.g. Google Sheets Drive-discovery
  // and month-unpivot fields). sectionVisibleFields is the shared predicate used by
  // SectionedConfigurationForm to decide which sections are non-empty.
  const visibleFields = sectionVisibleFields(section, schemaFields, values);

  return (
    <div
      className={cols === 2 ? 'grid grid-cols-2 gap-x-5 gap-y-4' : 'grid grid-cols-1 gap-y-4'}
      role="group"
    >
      {visibleFields.map((name) => {
        const synthetic = section.syntheticFields?.[name];
        const schemaField = byName.get(name);

        const fullWidth = cols === 2 && (
          synthetic !== undefined ||
          (schemaField !== undefined && FULL_WIDTH_TYPES.has(schemaField.type))
        );

        const isDirty = !!dirty[name];
        const subheading = section.subheadings?.[name];
        const spanClass = cols === 2 ? 'col-span-2' : '';

        return (
          <Fragment key={name}>
            {subheading && (
              <div
                className={`${spanClass} mt-1 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}
              >
                {subheading}
                <span aria-hidden className="h-px flex-1 bg-border" />
              </div>
            )}
            <div className={fullWidth ? 'col-span-2' : ''}>
              {synthetic ? (
                <FieldRenderer
                  synthetic={synthetic}
                  name={name}
                  connectorType={connectorType}
                  connectorName={connectorName}
                  value={values[name]}
                  onChange={(v) => onChange(name, v)}
                  disabled={disabled}
                  allValues={values}
                  onBatchFieldChange={onBatchFieldChange}
                  dirty={isDirty}
                />
              ) : (
                <>
                  <FieldRenderer
                    field={schemaField!}
                    value={values[name]}
                    onChange={(v) => onChange(name, v)}
                    disabled={disabled}
                    stored={credentialFlags[STORED_FLAG_BY_FIELD[name]] ?? false}
                    connectorType={connectorType}
                    connectorName={connectorName}
                    allValues={values}
                    onBatchFieldChange={onBatchFieldChange}
                    credentialFlags={credentialFlags}
                    oauthIdentity={oauthIdentity}
                    onReconnected={onReconnected}
                    dirty={isDirty}
                    error={errors?.[name]}
                    loaderResults={loaderResults}
                    onLoaderResult={onLoaderResult}
                  />
                  {(() => {
                    const Enhancement = getFieldEnhancement(connectorType, name);
                    return Enhancement ? (
                      <Enhancement
                        connectorType={connectorType}
                        fieldName={name}
                        formValues={values}
                        connectorName={connectorName}
                        onFieldChange={(n, val) => {
                          if (n === name) onChange(name, val);
                        }}
                      />
                    ) : null;
                  })()}
                </>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
